// Opens booking pages (/book-flight, /book-bus) in a NEW TAB while keeping the
// router state those pages expect (fare quote, bus segment, etc.).
//
// Router state does not survive a new tab, so we hand it over through
// localStorage: the new tab opens on /open-booking?h=<id>, waits for the
// payload under that id, then navigates to the real page with the same state.
//
// Flight flow (async fare quote):
//   const tab = openBookingTab();            // synchronously, inside the click
//   ...await fetchFareQuote(...)
//   deliverBookingTab(tab, navigate, url, { state })   // on success
//   cancelBookingTab(tab)                               // on error
//
// Bus flow (no async work):
//   openInNewTab(navigate, '/book-bus', { state })
//
// If the browser blocks the popup (or there is no user gesture, e.g. after
// login), everything falls back to normal same-tab navigation.

const KEY_PREFIX = 'rc_booking_handoff:';
const MAX_AGE_MS = 10 * 60 * 1000;

const newId = () =>
    Date.now().toString(36) + Math.random().toString(36).slice(2, 8);

const write = (id, payload) => {
    try {
        localStorage.setItem(KEY_PREFIX + id, JSON.stringify({ ...payload, ts: Date.now() }));
        return true;
    } catch (e) {
        return false;
    }
};

// Drop handoffs that were never picked up (tab closed early, etc.)
const sweep = () => {
    try {
        const now = Date.now();
        for (let i = localStorage.length - 1; i >= 0; i--) {
            const k = localStorage.key(i);
            if (!k || !k.startsWith(KEY_PREFIX)) continue;
            try {
                const v = JSON.parse(localStorage.getItem(k));
                if (!v?.ts || now - v.ts > MAX_AGE_MS) localStorage.removeItem(k);
            } catch (e) {
                localStorage.removeItem(k);
            }
        }
    } catch (e) { /* storage unavailable */ }
};

/** Call synchronously inside the click handler. Returns a handle (tab may be null if blocked). */
export const openBookingTab = () => {
    sweep();
    const id = newId();
    let tab = null;
    try {
        // No 'noopener': we need the handle to close the tab if booking fails
        tab = window.open(`/open-booking?h=${id}`, '_blank');
    } catch (e) {
        tab = null;
    }
    return { id, tab };
};

/** Send the prepared booking page to the tab. Falls back to same-tab navigation. */
export const deliverBookingTab = (handle, navigate, path, options = {}) => {
    const state = options.state ?? null;

    // No pre-opened tab (logged-out retry, popup blocked): try one now, else same tab
    if (!handle || !handle.tab) {
        openInNewTab(navigate, path, options);
        return;
    }
    // User closed the waiting tab before the fare check finished: treat as cancelled
    if (handle.tab.closed) return;

    if (!write(handle.id, { path, state })) {
        try { handle.tab.close(); } catch (e) { /* ignore */ }
        navigate(path, options);
    }
};

/** Booking could not start: close the waiting tab. */
export const cancelBookingTab = (handle) => {
    if (!handle) return;
    write(handle.id, { cancel: true });
    try { if (handle.tab && !handle.tab.closed) handle.tab.close(); } catch (e) { /* ignore */ }
};

/** One-shot: open path in a new tab with router state (used for bus). */
export const openInNewTab = (navigate, path, options = {}) => {
    const id = newId();
    const state = options.state ?? null;
    if (write(id, { path, state })) {
        let tab = null;
        try { tab = window.open(`/open-booking?h=${id}`, '_blank'); } catch (e) { tab = null; }
        if (tab) return;
        try { localStorage.removeItem(KEY_PREFIX + id); } catch (e) { /* ignore */ }
    }
    navigate(path, options);
};

/** Used by the /open-booking page. */
export const readBookingHandoff = (id) => {
    if (!id) return null;
    try {
        const raw = localStorage.getItem(KEY_PREFIX + id);
        if (!raw) return null;
        localStorage.removeItem(KEY_PREFIX + id);
        return JSON.parse(raw);
    } catch (e) {
        return null;
    }
};
